import Box from "@mui/material/Box";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import { Button, Grid } from "@mui/material";
import { AppDispatch } from "src/store";
import { selectCustomerReports } from "src/slices/customers/selectors";
import { downloadCustomerReport } from "src/slices/customers/thunks";
import { ICustomerReport } from "src/slices/customers/types";
import { format } from "date-fns";
import { Formik, FormikProps } from "formik";
import { ContainedButton } from "src/components/button-group";
import FormikControl from "src/components/formik/FormikControl";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { CSVLink } from "react-csv";
import { fetchLocations } from "src/slices/configuration/thunks";
import { selectLocations } from "src/slices/configuration/selectors";

export const CustomerReportPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const reports = useSelector(selectCustomerReports);
  const locations = useSelector(selectLocations);

  const formikRef = useRef<FormikProps<any>>(null);

  const columns: MRT_ColumnDef<ICustomerReport & { branchName: string }>[] = [
    { accessorKey: "id", header: "ID", size: 70 },
    { accessorKey: "name", header: "Name", size: 100 },
    { accessorKey: "primaryNumber", header: "Phone", size: 120 },
    { accessorKey: "branchName", header: "Branch", size: 120 },
    { accessorKey: "orderCreatedByCustomer", header: "Total Orders", size: 80 },
    { accessorKey: "lastOrderDate", header: "Last Order", size: 100 },
    { accessorKey: "totalComplaints", header: "Total Complaints", size: 80 },
    { accessorKey: "createdAt", header: "Created At", size: 100 },
  ];

  const [rows, setRows] = useState<ICustomerReport[]>([]);

  // 🔹 Fetch locations on mount
  useEffect(() => {
    dispatch(fetchLocations());
  }, [dispatch]);

  // 🔹 Fetch default report (last 7 days, all branches)
  useEffect(() => {
    const payload = {
      startDate: format(
        new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        "yyyy-MM-dd"
      ),
      endDate: format(new Date(), "yyyy-MM-dd"),
      branchId: 0,
    };
    dispatch(downloadCustomerReport(payload));
  }, [dispatch]);

  // 🔹 Update rows when reports change
  useEffect(() => {
    if (!reports?.data) return;
    const tableRows = reports.data.map((report) => ({
      ...report,
      lastOrderDate: report.lastOrderDate || "-",
      totalComplaints: report.totalComplaints ?? "No",
    }));
    setRows(tableRows);
  }, [reports]);

  return (
    <Box sx={{ width: 1 }}>
      <MaterialReactTable
        columns={columns}
        data={rows}
        initialState={{
          density: "compact",
          pagination: { pageIndex: 0, pageSize: 10 },
        }}
        renderTopToolbarCustomActions={({ table }) => (
          <Box
            sx={{ display: "flex", gap: "1rem", p: "0.5rem", flexWrap: "wrap" }}
          >
            <Formik
              initialValues={{
                startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
                endDate: new Date(),
                branchId: 0,
              }}
              innerRef={formikRef}
              onSubmit={() => {}}
            >
              {({ values, touched, errors, setFieldValue }) => (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormikControl
                      control="CalendarTime"
                      label="Start Date"
                      dateOrTimeOnly="date"
                      name="startDate"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormikControl
                      control="CalendarTime"
                      label="End Date"
                      dateOrTimeOnly="date"
                      name="endDate"
                    />
                  </Grid>

                  {/* 🔹 Branch Dropdown with "All" */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormikControl
                      control="SelectField"
                      name="branchId"
                      label="Branch"
                      options={[
                        { value: 0, label: "All" },
                        ...(Array.isArray(locations)
                          ? locations.map((branch) => ({
                              value: branch.id,
                              label: branch.name,
                            }))
                          : []),
                      ]}
                      handleChange={(e: any) =>
                        setFieldValue("branchId", Number(e.target.value))
                      }
                      error={Boolean(touched.branchId && errors.branchId)}
                      helperText={touched.branchId && errors.branchId}
                    />
                  </Grid>

                  <Grid item>
                    <ContainedButton
                      onClick={() => {
                        const payload = {
                          startDate: format(values.startDate, "yyyy-MM-dd"),
                          endDate: format(values.endDate, "yyyy-MM-dd"),
                          branchId: values.branchId ?? 0,
                        };
                        dispatch(downloadCustomerReport(payload));
                      }}
                    >
                      Search
                    </ContainedButton>
                  </Grid>
                </Grid>
              )}
            </Formik>

            {/* 🔹 CSV Export */}
            <CSVLink
              headers={columns.map((c) => ({ label: c.header, key: c.accessorKey! }))}
              data={rows}
              filename={`customer_report_${format(new Date(), "yyyyMMdd")}.csv`}
            >
              <Button
                disabled={table.getPrePaginationRowModel().rows.length === 0}
                startIcon={<FileDownloadIcon />}
                variant="contained"
              >
                Export All
              </Button>
            </CSVLink>
          </Box>
        )}
      />
    </Box>
  );
};
